import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  CalendarDays,
  Clock,
  Trophy,
  Phone,
  Users,
  ArrowRight,
} from "lucide-react";

import AllPlayer from "./AllPlayer";

const TARGET_DATE = new Date("2026-10-25T00:00:00");

export default function Countdown() {
  const getTimeLeft = () => {
    const diff = TARGET_DATE - new Date();

    if (diff <= 0) return null;

    return {
      Days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      Hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
      Minutes: Math.floor((diff / (1000 * 60)) % 60),
      Seconds: Math.floor((diff / 1000) % 60),
    };
  };

  const [time, setTime] = useState(getTimeLeft());

  useEffect(() => {
    const timer = setInterval(() => {
      setTime(getTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  /*
   |--------------------------------------------------------------------------
   | Registration Closed
   |--------------------------------------------------------------------------
   */

  if (!time) {
    return (
      <section className="relative w-full overflow-hidden bg-gradient-to-br from-gray-950 via-red-950 to-black py-12 md:py-16">

        {/* Background Glow */}
        <div className="pointer-events-none absolute -left-20 top-10 h-72 w-72 rounded-full bg-red-600/20 blur-3xl" />
        <div className="pointer-events-none absolute -right-20 bottom-10 h-72 w-72 rounded-full bg-orange-500/20 blur-3xl" />

        <div className="relative z-10 mx-auto w-[94%] max-w-7xl">

          {/* Closed Message */}
          <div className="mx-auto max-w-3xl text-center">

            <div className="mb-5 flex justify-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full border border-red-400/30 bg-red-500/10">
                <Trophy className="h-8 w-8 text-yellow-400" />
              </div>
            </div>

            <p className="mb-3 text-sm font-bold uppercase tracking-[0.3em] text-orange-400">
              BPL Season 2
            </p>

            <h2 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl md:text-5xl">
              Registration is Closed!
            </h2>

            <p className="mt-4 text-base text-gray-300 sm:text-lg">
              Player registration for the Badgaw Premier League Season 2
              has ended.
            </p>

            <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-yellow-400/20 bg-yellow-400/10 px-5 py-2.5 text-sm font-semibold text-yellow-300">
              <CalendarDays className="h-4 w-4" />
              Wait for the Auction Date
            </div>
          </div>

          {/* Players */}
          <div className="relative z-10 mt-10">
            <AllPlayer />
          </div>

        </div>
      </section>
    );
  }

  /*
   |--------------------------------------------------------------------------
   | Registration Countdown
   |--------------------------------------------------------------------------
   */

  return (
    <section className="relative w-full overflow-hidden bg-gradient-to-br from-gray-950 via-red-950 to-black py-10 md:py-16">

      {/* Background Effects */}
      <div className="pointer-events-none absolute -left-32 top-20 h-96 w-96 rounded-full bg-red-600/20 blur-3xl" />

      <div className="pointer-events-none absolute -right-32 bottom-10 h-96 w-96 rounded-full bg-orange-500/20 blur-3xl" />

      <div className="relative z-10 mx-auto w-[94%] max-w-7xl">

        {/* Header */}
        <div className="mx-auto max-w-3xl text-center">

          <div className="mb-4 flex items-center justify-center gap-2 text-orange-400">
            <Trophy className="h-5 w-5" />

            <span className="text-sm font-bold uppercase tracking-[0.25em]">
              BPL Season 2
            </span>

            <Trophy className="h-5 w-5" />
          </div>

          <h2 className="text-3xl font-black uppercase tracking-tight text-white sm:text-4xl md:text-5xl">
            Registration Closes In
          </h2>

          <p className="mt-3 text-sm text-gray-400 sm:text-base">
            Register now and become part of the Badgaw Premier League
            Season 2.
          </p>

        </div>

        {/* Countdown */}
        <div className="mx-auto mt-8 grid w-full max-w-4xl grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-5 md:mt-10">

          {Object.entries(time).map(([label, value]) => (
            <div
              key={label}
              className="group rounded-2xl bg-gradient-to-br from-yellow-400 via-orange-500 to-red-600 p-[2px] shadow-xl shadow-red-950/40 transition-transform duration-300 hover:-translate-y-1"
            >
              <div className="flex h-full flex-col items-center justify-center rounded-2xl bg-black/95 px-3 py-5 sm:py-7">

                <span className="text-4xl font-black tabular-nums text-white sm:text-5xl md:text-6xl">
                  {String(value).padStart(2, "0")}
                </span>

                <span className="mt-2 text-[10px] font-bold uppercase tracking-[0.2em] text-yellow-300 sm:text-xs md:text-sm">
                  {label}
                </span>

              </div>
            </div>
          ))}

        </div>

        {/* Registration CTA */}
        <div className="mt-8 flex justify-center md:mt-10">

          <Link
            to="/form"
            className="group inline-flex items-center gap-3 rounded-full bg-gradient-to-r from-red-600 to-orange-500 px-7 py-3.5 text-sm font-extrabold uppercase tracking-wide text-white shadow-lg shadow-red-900/40 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-red-900/60 sm:px-9 sm:py-4"
          >
            <Trophy className="h-5 w-5" />

            Register Now

            <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>

        </div>

        {/* Players */}
        <div className="relative z-10 mt-10 md:mt-14">
          <div className="mb-6 flex items-center justify-center gap-2">

            <Users className="h-5 w-5 text-orange-400" />

            <h3 className="text-lg font-bold text-white sm:text-xl">
              Registered Players
            </h3>

          </div>

          <AllPlayer />
        </div>

        {/* Sponsor / Partnership */}
        <div className="mx-auto mt-10 max-w-2xl rounded-2xl border border-white/10 bg-white/5 p-5 text-center backdrop-blur-sm md:mt-14 md:p-6">

          <div className="flex items-center justify-center gap-2 text-yellow-400">

            <Phone className="h-5 w-5" />

            <span className="font-bold">
              Sponsor / Partnership
            </span>

          </div>

          <p className="mt-2 text-sm text-gray-400">
            Interested in sponsoring BPL Season 2?
          </p>

          <a
            href="tel:+919973532109"
            className="mt-2 inline-block text-lg font-bold text-white transition-colors hover:text-orange-400 sm:text-xl"
          >
            +91 9973532109
          </a>

        </div>

      </div>
    </section>
  );
}